/**
 * This file used for style utilities,
 * it's help build custom UI components with style props without create html style
 * reference how to use: {@link https://styled-system.com/}
 */

/**
 * display styles utilities
 * @typedef {{ display: string, boxShadow: string, visibility: string }} DisplayProps
 * @param {DisplayProps} props
 * @returns
 */
export const display = (props) => ({
  display: props.display,
  boxShadow: props.boxShadow,
  visibility: props.visibility,
});

/**
 * sizing styles utilities
 * @typedef {{ width: string|number, height: string|number, maxWidth: string|number, minWidth: string|number, maxHeight: string|number, minHeight: string|number }} SizingProps
 * @param {SizingProps} props
 * @returns
 */
export const sizing = (props) => ({
  width: props.width,
  height: props.height,
  maxWidth: props.maxWidth,
  minWidth: props.minWidth,
  maxHeight: props.maxHeight,
  minHeight: props.minHeight,
});

/**
 * spacing styles utilities
 * @typedef {{
 * p: string|number, padding: string|number,
 * pt: string|number, paddingTop: string|number,
 * pr: string|number, paddingRight: string|number,
 * pb: string|number, paddingBottom: string|number,
 * pl: string|number, paddingLeft: string|number,
 * px: string|number, py: string|number,
 * m: string|number, margin: string|number,
 * mt: string|number, marginTop: string|number,
 * mr: string|number, marginRight: string|number,
 * mb: string|number, marginBottom: string|number,
 * ml: string|number, marginLeft: string|number,
 * mx: string|number, my: string|number,
 * }} SpacingProps
 * @param {SpacingProps} props
 * @returns
 */
export const spacing = (props) => ({
  padding: props.p ?? props.padding,
  paddingTop: props.pt ?? props.paddingTop ?? props.py,
  paddingRight: props.pr ?? props.paddingRight ?? props.px,
  paddingBottom: props.pb ?? props.paddingBottom ?? props.py,
  paddingLeft: props.pl ?? props.paddingLeft ?? props.px,
  margin: props.m ?? props.margin,
  marginTop: props.mt ?? props.marginTop ?? props.my,
  marginRight: props.mr ?? props.marginRight ?? props.mx,
  marginBottom: props.mb ?? props.marginBottom ?? props.my,
  marginLeft: props.ml ?? props.marginLeft ?? props.mx,
});

/**
 * color styles utilities
 * @typedef {{ color: string, bg: string|number, backgroundColor: string|number }} ColorProps
 * @param {ColorProps} props
 * @returns
 */
export const color = (props) => ({
  color: props.color,
  backgroundColor: props.bg || props.backgroundColor,
});

/**
 * @typedef {{
 * border: string,
 * borderWidth: number|string,
 * borderStyle: string,
 * borderColor: string,
 * borderRadius: number|string,
 * borderTop: string,
 * borderTopWidth: number|string,
 * borderTopStyle: string,
 * borderTopColor: string,
 * borderTopLeftRadius: number|string,
 * borderTopRightRadius: number|string,
 * borderRight: string,
 * borderRightWidth: number|string,
 * borderRightStyle: string,
 * borderRightColor: string,
 * borderBottom: string,
 * borderBottomWidth: number|string,
 * borderBottomStyle: string,
 * borderBottomColor: string,
 * borderBottomLeftRadius: number|string,
 * borderBottomRightRadius: number|string,
 * borderLeft: string,
 * borderLeftWidth: number|string,
 * borderLeftStyle: string,
 * borderLeftColor: string
 * }} BorderProps
 * @param {BorderProps} props
 * @returns
 */
export const border = (props) => ({
  border: props.border,
  borderWidth: props.borderWidth,
  borderStyle: props.borderStyle,
  borderColor: props.borderColor,
  borderRadius: props.borderRadius,
  borderTop: props.borderTop,
  borderTopWidth: props.borderTopWidth,
  borderTopStyle: props.borderTopStyle,
  borderTopColor: props.borderTopColor,
  borderTopLeftRadius: props.borderTopLeftRadius,
  borderTopRightRadius: props.borderTopRightRadius,
  borderRight: props.borderRight,
  borderRightWidth: props.borderRightWidth,
  borderRightStyle: props.borderRightStyle,
  borderRightColor: props.borderRightColor,
  borderBottom: props.borderBottom,
  borderBottomWidth: props.borderBottomWidth,
  borderBottomStyle: props.borderBottomStyle,
  borderBottomColor: props.borderBottomColor,
  borderBottomLeftRadius: props.borderBottomLeftRadius,
  borderBottomRightRadius: props.borderBottomRightRadius,
  borderLeft: props.borderLeft,
  borderLeftWidth: props.borderLeftWidth,
  borderLeftStyle: props.borderLeftStyle,
  borderLeftColor: props.borderLeftColor,
});
/**
 * @typedef {{
 * display: string
 * alignItems: string,
 * alignContent: string,
 * justifyItems: string,
 * justifyContent: string,
 * flexFlow: string,
 * flexWrap: string,
 * flexDirection: string,
 * flex: string | number,
 * flexGrow: string | number,
 * flexShrink: string | number,
 * flexBasis: string | number,
 * justifySelf: string | number,
 * alignSelf: string | number,
 * order: string | number,
 * }} FlexboxProps
 * @param {FlexboxProps} props
 * @returns
 */
export const flexbox = (props) => ({
  display:
    props.alignItems?.length > 0 ||
    props.alignContent?.length > 0 ||
    props.justifyItems?.length > 0 ||
    props.justifyContent?.length > 0 ||
    props.flexFlow?.length > 0 ||
    props.flexWrap?.length > 0 ||
    props.flexDirection?.length > 0
      ? 'flex'
      : props.display,
  alignItems: props.alignItems,
  alignContent: props.alignContent,
  justifyItems: props.justifyItems,
  justifyContent: props.justifyContent,
  flexFlow: props.flexFlow,
  flexWrap: props.flexWrap,
  flexDirection: props.flexDirection,
  flex: props.flex,
  flexGrow: props.flexGrow,
  flexShrink: props.flexShrink,
  flexBasis: props.flexBasis,
  justifySelf: props.justifySelf,
  alignSelf: props.alignSelf,
  order: props.order,
});

/**
 * Typography styles utilities
 * @typedef {{
 * fontFamily: string|number,
 * fontSize: string|number,
 * fontWeight: string|number,
 * lineHeight: string|number,
 * letterSpacing: string|number,
 * textAlign: string,
 * align: string,
 * fontStyle: string,}} TypographyProps
 * @param {TypographyProps}  props
 * @returns
 */
export const typography = (props) => ({
  fontFamily: props.fontFamily,
  fontSize: props.fontSize,
  fontWeight: props.fontWeight,
  lineHeight: props.lineHeight,
  letterSpacing: props.letterSpacing,
  textAlign: props.textAlign || props.align,
  fontStyle: props.fontStyle,
  textTransform: props.textTransform,
});
